/**
 * RBAC Engine — Role-Based Access Control with Inheritance
 *
 * Architecture:
 *   Permissions are granular string literals: 'cases.view', 'cases.view.all'
 *   Roles inherit permissions from parent roles
 *   Inheritance is resolved once at startup — O(1) per-check via Set lookup
 *   Explicit denies override inherited grants (deny wins)
 *
 * Extending:
 *   1. Add a new Permission to the Permission union
 *   2. Add it to the appropriate role's `grants` array
 *   3. Inherited roles get it automatically
 *
 * Access model:
 *   - Role controls what a person may do.
 *   - Record assignment controls which client records they may do it to.
 *   - Admin status is the normal bypass for cross-record access.
 *   - Per-user grants can be used for narrowly approved exceptions.
 */

// ── Permission definitions ────────────────────────────────────
export type Permission =
  // Navigation
  | 'nav.inventory'      | 'nav.donors'      | 'nav.partners'
  | 'nav.volunteers'     | 'nav.staff'       | 'nav.audit'
  | 'nav.reports'        | 'nav.settings'    | 'nav.inquiries'
  // Cases
  | 'cases.view'         | 'cases.view.all'  | 'cases.create'
  | 'cases.edit'         | 'cases.delete'    | 'cases.reassign' | 'cases.close'
  | 'cases.notes.view'   | 'cases.notes.add' | 'cases.notes.delete'
  | 'cases.tasks.manage' | 'cases.appointments.manage'
  // Households
  | 'households.view'    | 'households.create' | 'households.edit'
  | 'households.delete'  | 'households.merge'
  // Inventory
  | 'inventory.view'     | 'inventory.manage'  | 'inventory.delete'
  | 'inventory.transact' | 'inventory.export'
  // Volunteers
  | 'volunteers.view'    | 'volunteers.manage'
  | 'volunteers.hours.approve' | 'volunteers.hours.reject'
  // Donors
  | 'donors.view'        | 'donors.manage'     | 'donors.financials'
  | 'donors.receipts'
  // Donations
  | 'donations.view'     | 'donations.create'  | 'donations.delete'
  // Partners
  | 'partners.view'      | 'partners.manage'   | 'partners.requests.approve'
  // Staff / Accounts
  | 'staff.view'         | 'staff.invite'      | 'staff.roles.edit'
  | 'accounts.create'    | 'accounts.deactivate'
  // Documents
  | 'documents.view'     | 'documents.upload'  | 'documents.delete'
  | 'documents.view.all'
  // Notifications
  | 'notifications.view' | 'notifications.broadcast' | 'notifications.manage'
  // Messages
  | 'messages.send'      | 'messages.view'     | 'messages.view.all'
  // Reports & Audit
  | 'reports.view'       | 'reports.export'    | 'audit.view'
  // Settings
  | 'settings.view'      | 'settings.manage'
  // Financial
  | 'financials.view'    | 'financials.manage'
  // System
  | 'system.admin'       | 'demo.create'

// ── Role IDs ─────────────────────────────────────────────────
export type RoleId =
  | 'executive_director'
  | 'operations_manager'
  | 'program_manager'
  | 'volunteer_coordinator'
  | 'staff'
  | 'employee'
  | 'volunteer'
  | 'partner_agency'
  | 'donor'
  | 'client'

// ── Role definitions with inheritance ────────────────────────
interface RoleDef {
  inherits: RoleId[]
  grants:   Permission[]
  denies?:  Permission[]
}

const ROLE_DEFS: Record<RoleId, RoleDef> = {

  // Base roles — no inheritance
  employee: {
    inherits: [],
    grants: [
      'cases.view', 'cases.notes.view', 'cases.notes.add',
      'cases.tasks.manage', 'cases.appointments.manage',
      'households.view', 'inventory.view',
      'messages.send', 'messages.view',
      'notifications.view',
      'documents.view', 'documents.upload',
    ],
  },

  volunteer: {
    inherits: [],
    grants: [
      'messages.send', 'messages.view',
      'notifications.view',
      'documents.view', 'documents.upload',
    ],
  },

  partner_agency: {
    inherits: [],
    grants: [
      'inventory.view',
      'partners.view',
      'messages.send', 'messages.view',
      'notifications.view',
      'documents.view', 'documents.upload',
    ],
  },

  donor: {
    inherits: [],
    grants: [
      'donations.view',
      'messages.send', 'messages.view',
      'notifications.view',
    ],
  },

  client: {
    inherits: [],
    grants: [
      'cases.view',
      'documents.view', 'documents.upload',
      'messages.send', 'messages.view',
      'notifications.view',
    ],
  },

  // Staff — extends employee
  staff: {
    inherits: ['employee'],
    grants: [
      'cases.create', 'cases.edit',
      'households.create', 'households.edit',
      'nav.inquiries',
    ],
  },

  // Volunteer coordinator — extends staff
  volunteer_coordinator: {
    inherits: ['staff'],
    grants: [
      'volunteers.view', 'volunteers.manage',
      'volunteers.hours.approve', 'volunteers.hours.reject',
      'nav.volunteers',
    ],
  },

  // Program manager — extends volunteer coordinator
  program_manager: {
    inherits: ['volunteer_coordinator'],
    grants: [
      'cases.close',
      'households.merge',
      'inventory.manage', 'inventory.transact', 'inventory.export',
      'partners.view', 'partners.manage',
      'documents.view.all',
      'messages.view.all',
      'reports.view', 'reports.export',
      'nav.inventory', 'nav.partners', 'nav.reports',
    ],
  },

  // Operations manager — extends program manager
  operations_manager: {
    inherits: ['program_manager'],
    grants: [
      'donors.view', 'donors.manage', 'donors.receipts',
      'donations.view', 'donations.create', 'donations.delete',
      'staff.view', 'staff.invite',
      'accounts.create', 'accounts.deactivate',
      'notifications.broadcast', 'notifications.manage',
      'partners.requests.approve',
      'audit.view',
      'settings.view',
      'financials.view',
      'demo.create',
      'nav.donors', 'nav.staff', 'nav.audit',
    ],
  },

  // Executive director — extends operations manager, full access
  executive_director: {
    inherits: ['operations_manager'],
    grants: [
      'donors.financials',
      'staff.roles.edit',
      'settings.manage',
      'financials.view', 'financials.manage',
      'system.admin',
      'nav.settings',
    ],
  },
}

// ── Permission resolution (runs once at startup) ──────────────
const RESOLVED_CACHE = new Map<RoleId, Set<Permission>>()
const DENY_CACHE     = new Map<RoleId, Set<Permission>>()

function resolveRole(roleId: RoleId, visited = new Set<RoleId>()): Set<Permission> {
  if (RESOLVED_CACHE.has(roleId)) return RESOLVED_CACHE.get(roleId)!
  if (visited.has(roleId)) return new Set()   // break circular refs
  visited.add(roleId)

  const def     = ROLE_DEFS[roleId]
  const granted = new Set<Permission>()
  const denied  = new Set<Permission>()

  // Inherit from parent roles first
  for (const parentId of def.inherits) {
    const parentGrants = resolveRole(parentId, visited)
    const parentDenies = DENY_CACHE.get(parentId) ?? new Set<Permission>()
    parentGrants.forEach(p => granted.add(p))
    parentDenies.forEach(p => denied.add(p))
  }

  // Apply own grants and denies
  def.grants.forEach(p => granted.add(p))
  def.denies?.forEach(p => { denied.add(p); granted.delete(p) })

  RESOLVED_CACHE.set(roleId, granted)
  DENY_CACHE.set(roleId, denied)
  return granted
}

// Pre-resolve all roles at module load time
Object.keys(ROLE_DEFS).forEach(r => resolveRole(r as RoleId))

// ── Public API ────────────────────────────────────────────────
/**
 * Check whether a role has a permission.
 * O(1) — uses pre-resolved Set lookup.
 */
export function roleHas(roleId: RoleId | string, permission: Permission): boolean {
  if (!(roleId in ROLE_DEFS)) return false
  return RESOLVED_CACHE.get(roleId as RoleId)?.has(permission) ?? false
}

/**
 * Get all permissions for a role (including inherited).
 */
export function getRolePermissions(roleId: RoleId | string): Set<Permission> {
  if (!(roleId in ROLE_DEFS)) return new Set()
  return RESOLVED_CACHE.get(roleId as RoleId) ?? new Set()
}

/**
 * Build a permission checker for a specific user context.
 * The is_admin flag grants ALL permissions regardless of role. Resource-level
 * restrictions are enforced separately by database RLS and record assignment.
 *
 * Usage:
 *   const check = buildChecker('staff', false)
 *   check('cases.view')      // → true
 *   check('system.admin')    // → false
 */
export function buildChecker(
  roleId: string,
  isAdmin: boolean,
  /** Extra permissions granted beyond role (future: per-user grants) */
  extras: Permission[] = []
) {
  const base = getRolePermissions(roleId)
  const extraSet = new Set(extras)

  return function can(permission: Permission): boolean {
    if (isAdmin) return true      // Admin bypass
    if (extraSet.has(permission)) return true
    return base.has(permission)
  }
}

export { ROLE_DEFS }
